import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {FileUploadService} from "./services/file-upload.service";
import {HttpEventType} from "@angular/common/http";
import {ClaimDto} from "../../../auto-generated-server-dtos/claim/claim-dto";
import {ListUploadedFilesService} from "./services/list-uploaded-files.service";
import {DeleteUploadedFileService} from "./services/delete-uploaded-file.service";
import heic2any from "heic2any";
import {AttachmentFile} from "../../../models/attachmentFile";
import Utils from "src/app/helpers/utils";
import {DownloadDocumentService} from "./services/download-document.service";

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent implements OnChanges {

  @Input() ClaimDto: ClaimDto | null = null;
  @Input() IsAdjusterView = false;
  files: AttachmentFile[] = [];
  uploadError = false;

  constructor(
    private fileUploadService: FileUploadService,
    private listUploadedFilesService: ListUploadedFilesService,
    private deleteUploadedFileService: DeleteUploadedFileService,
    private utils: Utils,
    private downloadDocumentService: DownloadDocumentService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.ClaimDto && changes.ClaimDto.currentValue) {
      this.listUploadedFiles();
    }
  }

  deleteUploadedFile(file: AttachmentFile) {
    this.deleteUploadedFileService.deleteUploadedFile(file.fileId).subscribe({
      next: () => {
        this.files = this.files.filter(x => x.fileId !== file.fileId);
      },
      error: (error) => {
        console.log(error);
      }
    });
  };

  listUploadedFiles() {
    this.listUploadedFilesService.listUploadedFiles(this.ClaimDto!.claimId).subscribe((files) => {
      for (let i = 0; i < files.length; i++) {
        this.files.push({
          name: files[i].originalFilenameWithExtension,
          type: files[i].mimeType,
          size: files[i].fileSize,
          icon: this.utils.getFileTypeIcon(files[i].originalFilenameWithExtension),
          progress: 0,
          convertingFile: false,
          fileId: files[i].documentId
        });
      }
    });
  }

  onFileDropped($event: FileList) {
    this.uploadFiles($event);
  }

  fileBrowseHandler($event: any) {
    this.uploadFiles($event.target.files);
  }

  private async uploadFiles(files: FileList) {
    this.uploadError = false;
    for (let i = 0; i < files.length; i++) {
      const isHeicFile = (/image\/hei(c|f)/.test(files[i].type));

      let attachmentFile: AttachmentFile = {
        name: files[i].name,
        type: files[i].type,
        size: files[i].size,
        icon: this.utils.getFileTypeIcon(files[i].name),
        progress: 0,
        convertingFile: isHeicFile,
        fileId: 0
      };
      this.files.push(attachmentFile);

      let file = isHeicFile
        ? await this.convertHeicToJpg(files[i])
        : files[i];

      // update file tag to show that file has been converted if file is originally HEIC
      if (isHeicFile) {
        attachmentFile.size = file.size;
        attachmentFile.name = file.name;
        attachmentFile.type = file.type;
        attachmentFile.icon = this.utils.getFileTypeIcon(file.name);
      }

      this.fileUploadService.fileUpload(file, this.ClaimDto!).subscribe({
        next: (response) => {
          if (response.type === HttpEventType.UploadProgress) {
            attachmentFile.progress = Math.floor(Math.round(100 * response.loaded / response.total!));
          } else if (response.type === HttpEventType.Response) {
            attachmentFile.fileId = parseInt(response.body!);
          }
        },
        error: () => {
          this.uploadError = true;
          this.files = this.files.filter(f => f !== attachmentFile);
        },
        complete: () => {
          attachmentFile.progress = 0;
          attachmentFile.convertingFile = false;
        }
      });
    }
  }

  private convertHeicToJpg(heicFile: File): Promise<File> {
    return new Promise((resolve) => {
      heic2any({
        blob: heicFile,
        toType: "image/jpeg",
        quality: 0.8
      })
      .then((result: Blob) => {
        const jpgName = heicFile.name.replace(/\.[^/.]+$/, ".jpg");
        const jpgFile = new File([result], jpgName, {
          type: 'image/jpeg',
        });
        resolve(jpgFile);
      });
    });
  }

  simulateFileUpload(file: AttachmentFile) {
    setTimeout(() => {
      if (file.progress < 100) {
        file.progress += 1;
        this.simulateFileUpload(file);
      } else {
        file.progress = 0;
      }
    }, 50);
  }

  viewDocument(file: AttachmentFile) {
    let windowRef = window.open();
    this.downloadDocumentService.downloadDocument(file.fileId).subscribe({
      next: (response) => {
        let downloadBlob = Utils.openBlobInNewTab(response, file.type);
        windowRef!.location = window.URL.createObjectURL(downloadBlob);
      },
      error: (error) => {
        console.log(error);
      },
      complete: () => {
      }
    });
  }

  protected readonly Utils = Utils;
}
